import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  DEFAULT_DOSE_GRACE_DAYS,
  DOSE_REQUIREMENT_LEVELS,
  SEX_ELIGIBILITY,
  humanise,
} from "config/immunizationConstants";

export const emptyDoseRule = () => ({
  ruleCode: "",
  label: "",
  antigenIds: [],
  doseSequence: 1,
  recommendedOffsetDays: "",
  minAgeDays: "",
  maxAgeDays: "",
  earliestValidAgeDays: "",
  minIntervalDays: "",
  recommendedIntervalDays: "",
  graceDays: DEFAULT_DOSE_GRACE_DAYS,
  overdueAfterDays: "",
  eligibility: {
    sex: "ANY",
    riskCohortIds: [],
    pregnancyOnly: false,
    occupationCodes: [],
  },
  permittedProductIds: [],
  requirement: "MANDATORY",
  notes: "",
  sequence: 0,
});

const DoseRuleEditor = ({
  value = [],
  onChange,
  antigens = [],
  products = [],
  cohorts = [],
}) => {
  const rules = Array.isArray(value) ? value : [];

  const updateRule = (index, patch) =>
    onChange(
      rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)),
    );

  const updateEligibility = (index, patch) =>
    updateRule(index, {
      eligibility: { ...rules[index].eligibility, ...patch },
    });

  const num = (label, index, key, extra = {}) => (
    <TextField
      label={label}
      type="number"
      size="small"
      sx={{ minWidth: 150 }}
      value={rules[index][key]}
      onChange={(event) => updateRule(index, { [key]: event.target.value })}
      {...extra}
    />
  );

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Dose Rules
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1.5 }}
      >
        Leave a field blank when it does not apply. Rule codes must be unique
        within the schedule — ledger entries reference a rule by its code.
      </Typography>

      {rules.map((rule, index) => (
        <Accordion key={index} disableGutters sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {rule.ruleCode || `Rule ${index + 1}`}
              </Typography>
              <Chip size="small" label={`Dose ${rule.doseSequence || 1}`} />
              {rule.antigenIds?.length > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${rule.antigenIds.length} antigen(s)`}
                />
              )}
              <Box sx={{ flex: 1 }} />
              <IconButton
                component="span"
                onClick={(event) => {
                  event.stopPropagation();
                  onChange(rules.filter((_, i) => i !== index));
                }}
              >
                <DeleteOutlineIcon fontSize="small" color="error" />
              </IconButton>
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="Rule Code"
                  required
                  size="small"
                  sx={{ minWidth: 180 }}
                  value={rule.ruleCode}
                  onChange={(event) =>
                    updateRule(index, { ruleCode: event.target.value })
                  }
                />
                <TextField
                  label="Label"
                  size="small"
                  sx={{ minWidth: 200 }}
                  value={rule.label}
                  onChange={(event) =>
                    updateRule(index, { label: event.target.value })
                  }
                />
                {num("Dose Sequence", index, "doseSequence", {
                  required: true,
                })}
                <TextField
                  select
                  label="Requirement"
                  size="small"
                  sx={{ minWidth: 160 }}
                  value={rule.requirement}
                  onChange={(event) =>
                    updateRule(index, { requirement: event.target.value })
                  }
                >
                  {DOSE_REQUIREMENT_LEVELS.map((level) => (
                    <MenuItem key={level} value={level}>
                      {humanise(level)}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                options={antigens}
                value={antigens.filter((a) =>
                  (rule.antigenIds || []).includes(a._id),
                )}
                getOptionLabel={(option) => option?.antigenName || ""}
                isOptionEqualToValue={(option, val) => option._id === val._id}
                onChange={(event, next) =>
                  updateRule(index, {
                    antigenIds: next.map((item) => item._id),
                  })
                }
                renderInput={(params) => (
                  <TextField {...params} label="Antigens" required />
                )}
              />

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {num(
                  "Offset from anchor (days)",
                  index,
                  "recommendedOffsetDays",
                )}
                {num("Minimum age (days)", index, "minAgeDays")}
                {num("Maximum age (days)", index, "maxAgeDays")}
                {num(
                  "Earliest valid age (days)",
                  index,
                  "earliestValidAgeDays",
                )}
              </Box>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {num("Minimum interval (days)", index, "minIntervalDays", {
                  disabled: Number(rule.doseSequence) === 1,
                  helperText:
                    Number(rule.doseSequence) === 1 ?
                      "Dose 1 has no previous dose"
                    : "",
                })}
                {num(
                  "Recommended interval (days)",
                  index,
                  "recommendedIntervalDays",
                  { disabled: Number(rule.doseSequence) === 1 },
                )}
                {num("Grace days", index, "graceDays")}
                {num("Overdue after (days)", index, "overdueAfterDays")}
              </Box>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  select
                  label="Sex Eligibility"
                  size="small"
                  sx={{ minWidth: 160 }}
                  value={rule.eligibility?.sex || "ANY"}
                  onChange={(event) =>
                    updateEligibility(index, { sex: event.target.value })
                  }
                >
                  {SEX_ELIGIBILITY.map((option) => (
                    <MenuItem key={option} value={option}>
                      {humanise(option)}
                    </MenuItem>
                  ))}
                </TextField>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(rule.eligibility?.pregnancyOnly)}
                      onChange={(event) =>
                        updateEligibility(index, {
                          pregnancyOnly: event.target.checked,
                        })
                      }
                    />
                  }
                  label="Pregnancy only"
                />
                {num("Sequence", index, "sequence")}
              </Box>

              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                options={cohorts}
                value={cohorts.filter((c) =>
                  (rule.eligibility?.riskCohortIds || []).includes(c._id),
                )}
                getOptionLabel={(option) => option?.cohortName || ""}
                isOptionEqualToValue={(option, val) => option._id === val._id}
                onChange={(event, next) =>
                  updateEligibility(index, {
                    riskCohortIds: next.map((item) => item._id),
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Restrict to Risk Cohorts"
                    helperText="Leave empty to apply to everyone"
                  />
                )}
              />

              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                options={products}
                value={products.filter((p) =>
                  (rule.permittedProductIds || []).includes(p._id),
                )}
                getOptionLabel={(option) => option?.brandName || ""}
                isOptionEqualToValue={(option, val) => option._id === val._id}
                onChange={(event, next) =>
                  updateRule(index, {
                    permittedProductIds: next.map((item) => item._id),
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Permitted Products"
                    helperText="Clinical ceiling — hospitals narrow this with their own formulary"
                  />
                )}
              />

              <TextField
                label="Notes"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={rule.notes}
                onChange={(event) =>
                  updateRule(index, { notes: event.target.value })
                }
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => onChange([...rules, emptyDoseRule()])}
      >
        Add dose rule
      </Button>
    </Box>
  );
};

export default DoseRuleEditor;
